import React, { useEffect } from 'react';
import { Col, Row } from 'antd/lib/grid';
import { useForm } from 'antd/lib/form/Form';
import { Form, FORM_ITEM_RULES, PHONE_MASK } from '@perxis-auto/forms';
import useAsyncAction from '@perxis-auto/forms/lib/hooks/useAsyncAction';
import { Input } from '@perxis/perxis-auto-ui-kit/lib/components/Input';
import { InputMask } from '@perxis/perxis-auto-ui-kit/lib/components/InputMask';
import { Select } from '@perxis/perxis-auto-ui-kit/lib/components/Select';
import { Button } from '@perxis/perxis-auto-ui-kit/lib/components/Button';
import { Checkbox } from '@perxis/perxis-auto-ui-kit/lib/components/Checkbox';
import { Icon } from '@perxis/perxis-auto-ui-kit/lib/components/Icon';
import { Typography } from '@perxis/perxis-auto-ui-kit/lib/components/Typography';
import { FormItem } from '@perxis/perxis-auto-ui-kit/lib/components/FormItem';

import { EVENTS_CHANNEL } from '@/apps/feedback-form/constants/events';
import { useDirectionLSDataFetch } from '@/apps/feedback-form/hooks';
import { sendFeedback } from '@/api/formService';
import { useDealerLSDataFetch, useModelDataFetch } from '@/hooks';
import config from '@/utils/config';
import { formatPhone, isExist } from '@/utils/common';
import { CUSTOM_RULES, VIN_MASK } from '@/utils/validateRules';
import { TERMS_CONDITIONS_URL } from '@/constants/links';
import { STATUS_MESSAGES } from '@/constants/common';
import { FORM_EVENTS, GEO_LOCATOR_CITY_SELECTED_EVENT } from '@/constants/events';
import { TSelectLabelInValue } from '@/types/common';
import ThankStep from '@/components/ThankStep';

import style from './style.module.scss';

type FeedbackFormProps = any;

export const FeedbackFormFields: Record<keyof FeedbackFormValues, string> = {
  last_name: 'last_name',
  first_name: 'first_name',
  phone: 'phone',
  email: 'email',
  city: 'city',
  dealer: 'dealer',
  model: 'model',
  vin: 'vin',
  direction_id: 'direction_id',
  type_id: 'type_id',
  reason_id: 'reason_id',
  notes: 'notes',
  confirm_personal_data_processing_policy: 'confirm_personal_data_processing_policy',
};

export type FeedbackFormValues = {
  last_name: string;
  first_name: string;
  email: string;
  phone: string;
  city: string | null;
  model: string | null;
  dealer: TSelectLabelInValue<number> | null;
  vin: string | null;
  direction_id: TSelectLabelInValue<number> | null;
  type_id: TSelectLabelInValue<number> | null;
  reason_id: TSelectLabelInValue<number> | null;
  notes: string;
  confirm_personal_data_processing_policy: boolean;
};

const initialValues: FeedbackFormValues = {
  last_name: '',
  first_name: '',
  email: '',
  phone: '',
  city: null,
  model: null,
  dealer: null,
  vin: null,
  direction_id: null,
  type_id: null,
  reason_id: null,
  notes: '',
  confirm_personal_data_processing_policy: false,
};

const FeedbackForm: React.VFC<FeedbackFormProps> = ({}: FeedbackFormProps) => {
  const [form] = useForm<FeedbackFormValues>();

  const {
    data: { cities },
    loading: dealerLoading,
    getDealersByCity,
  } = useDealerLSDataFetch();

  const {
    data: { models },
    loading: modelLoading,
  } = useModelDataFetch();

  const {
    data: { directions },
    loading: directionLoading,
    getFeedbackTypes,
    getFeedbackReasons,
  } = useDirectionLSDataFetch();

  const [vinValidator, isValidating] = useAsyncAction(
    (CUSTOM_RULES.vin as any).validator,
  );

  const onFinish = async (values: any) => {
    await sendFeedback({
      ...values,
      phone: formatPhone(values?.phone),
    });
  };

  const getGeoLocatorValues = (city: string, resetValues = false) => {
    const isExistCity = isExist(city, cities);
    if (!city || !isExistCity) {
      return resetValues
        ? {
            city: null,
            dealer: null,
          }
        : {};
    }
    const filteredDealers = getDealersByCity(city);
    const isSingleDealer = filteredDealers.length === 1;
    return {
      city,
      dealer: isSingleDealer
        ? {
            value: filteredDealers[0].id,
            label: filteredDealers[0].title,
          }
        : null,
    };
  };

  const geoLocatorEventsHandler = (eventType: string, eventData: any) => {
    switch (eventType) {
      case GEO_LOCATOR_CITY_SELECTED_EVENT: {
        const city = eventData?.city;
        return getGeoLocatorValues(city);
      }
      default:
        return {};
    }
  };

  useEffect(() => {
    if (!dealerLoading) {
      const geoValues = getGeoLocatorValues(form.getFieldValue('city'), true);
      form.setFieldsValue(geoValues);
    }
  }, [dealerLoading]);

  return (
    <Form<FeedbackFormValues>
      form={form}
      formEvents={FORM_EVENTS}
      formChannel={EVENTS_CHANNEL}
      initialValues={{
        ...initialValues,
        ...config.get('initialValues'),
      }}
      onFinish={onFinish}
      className={style.form}
      finalStep={() => <ThankStep />}
      geoLocatorEventsHandler={geoLocatorEventsHandler}
      successCallback={config.get('successCallback')}
      errorCallback={config.get('errorCallback')}
      formatValues={config.get('formatValues')}
      defaultMessages={STATUS_MESSAGES}
    >
      {({ isSubmitting, values, changeFieldValue, status }) => {
        const { city, dealer, direction_id, type_id, reason_id } = values || {};
        const filteredDealers = getDealersByCity(city);
        const filteredFeedbackTypes = getFeedbackTypes(direction_id?.value);
        const filteredFeedbackReasons = getFeedbackReasons(
          direction_id?.value,
          type_id?.value,
        );
        const showErrorMessage = status?.type === 'error' && !!status.message;

        const isDisabledButton = isSubmitting || isValidating;

        return (
          <>
            <Typography.Title className={style.title} level={2} classLevel={1}>
              обратная связь
            </Typography.Title>
            <Row
              gutter={[
                { sm: 40, xs: 0 },
                { sm: 30, xs: 20 },
              ]}
            >
              <Col sm={12} xs={24}>
                <FormItem
                  label="Имя"
                  name={FeedbackFormFields.first_name}
                  rules={[
                    FORM_ITEM_RULES.required,
                    FORM_ITEM_RULES.whitespace,
                    FORM_ITEM_RULES.minLength(2),
                    FORM_ITEM_RULES.maxLength(25),
                  ]}
                >
                  <Input />
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem
                  label="Фамилия"
                  name={FeedbackFormFields.last_name}
                  rules={[
                    FORM_ITEM_RULES.required,
                    FORM_ITEM_RULES.whitespace,
                    FORM_ITEM_RULES.minLength(2),
                    FORM_ITEM_RULES.maxLength(25),
                  ]}
                >
                  <Input />
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem
                  label="Телефон"
                  name={FeedbackFormFields.phone}
                  rules={[
                    FORM_ITEM_RULES.required,
                    FORM_ITEM_RULES.whitespace,
                    FORM_ITEM_RULES.phone,
                    FORM_ITEM_RULES.maxLength(20),
                  ]}
                >
                  <InputMask
                    type="tel"
                    mask={PHONE_MASK}
                    maskOptions={{
                      lazy: true,
                      placeholderChar: 'X',
                    }}
                  />
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem
                  label="E-mail"
                  name={FeedbackFormFields.email}
                  rules={[
                    FORM_ITEM_RULES.required,
                    FORM_ITEM_RULES.whitespace,
                    FORM_ITEM_RULES.email,
                    FORM_ITEM_RULES.maxLength(255),
                  ]}
                >
                  <Input />
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem
                  label="Город"
                  name={FeedbackFormFields.city}
                  rules={[FORM_ITEM_RULES.required]}
                  loading={dealerLoading}
                >
                  <Select
                    disabled={dealerLoading}
                    loading={dealerLoading}
                    onChange={city => {
                      if (dealer) changeFieldValue({ dealer: null });

                      if (city) {
                        const filteredDealers = getDealersByCity(city);
                        filteredDealers.length === 1 &&
                          changeFieldValue({
                            dealer: {
                              value: filteredDealers[0].id,
                              label: filteredDealers[0].title,
                            },
                          });
                      }
                    }}
                  >
                    {cities.map(city => (
                      <Select.Option key={city} value={city}>
                        {city}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem
                  label="Дилер"
                  name={FeedbackFormFields.dealer}
                  rules={[FORM_ITEM_RULES.required]}
                  loading={dealerLoading}
                >
                  <Select
                    labelInValue
                    disabled={!city || dealerLoading}
                    loading={dealerLoading}
                  >
                    {filteredDealers.map(({ id, title }) => (
                      <Select.Option key={id} value={id}>
                        {title}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem
                  label="Модель"
                  name={FeedbackFormFields.model}
                  rules={[
                    FORM_ITEM_RULES.required,
                    FORM_ITEM_RULES.minLength(3),
                    FORM_ITEM_RULES.maxLength(35),
                  ]}
                >
                  <Select disabled={modelLoading} loading={modelLoading}>
                    {models.map(model => (
                      <Select.Option key={model.id} value={model.title}>
                        {model.title}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem
                  label="VIN"
                  name={FeedbackFormFields.vin}
                  rules={[
                    FORM_ITEM_RULES.required,
                    FORM_ITEM_RULES.whitespace,
                    { ...CUSTOM_RULES.vin, validator: vinValidator },
                    FORM_ITEM_RULES.minLength(6),
                    FORM_ITEM_RULES.maxLength(17),
                  ]}
                >
                  <InputMask
                    placeholder="Введите"
                    mask={VIN_MASK}
                    maskOptions={{
                      lazy: true,
                      placeholderChar: ' ',
                      definitions: {
                        '*': /[A-Za-z0-9.,^$*#()_+-]/,
                      },
                    }}
                  />
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem
                  label="Направление обращения"
                  name={FeedbackFormFields.direction_id}
                  rules={[FORM_ITEM_RULES.required]}
                >
                  <Select
                    disabled={directionLoading}
                    loading={directionLoading}
                    onChange={(direction: TSelectLabelInValue<number>) => {
                      if (!!type_id || !!reason_id) {
                        changeFieldValue({ type_id: null, reason_id: null });
                      }
                      if (direction) {
                        const feedbackTypes = getFeedbackTypes(direction.value);
                        if (feedbackTypes.length === 1) {
                          const type = feedbackTypes[0];
                          const reasonId =
                            type.reasons.length === 1
                              ? {
                                  value: type.reasons[0].id,
                                  label: type.reasons[0].title,
                                }
                              : null;
                          changeFieldValue({
                            type_id: {
                              value: type.id,
                              label: type.title,
                            },
                            reason_id: reasonId,
                          });
                        }
                      }
                    }}
                    labelInValue
                  >
                    {directions.map(({ id, title }) => (
                      <Select.Option key={id} value={id}>
                        {title}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>
              {!!filteredFeedbackTypes.length && (
                <Col sm={12} xs={24}>
                  <FormItem
                    label="Тип обращения"
                    name={FeedbackFormFields.type_id}
                    rules={[FORM_ITEM_RULES.required]}
                  >
                    <Select
                      onChange={(typeId: TSelectLabelInValue<number>) => {
                        if (reason_id) {
                          changeFieldValue({ reason_id: null });
                        }

                        if (typeId) {
                          const feedbackReasons = getFeedbackReasons(
                            direction_id?.value,
                            typeId.value,
                          );
                          feedbackReasons.length === 1 &&
                            changeFieldValue({
                              reason_id: {
                                value: feedbackReasons[0].id,
                                label: feedbackReasons[0].title,
                              },
                            });
                        }
                      }}
                      labelInValue
                    >
                      {filteredFeedbackTypes.map(({ id, title }) => (
                        <Select.Option key={id} value={id}>
                          {title}
                        </Select.Option>
                      ))}
                    </Select>
                  </FormItem>
                </Col>
              )}
              {!!filteredFeedbackReasons.length && (
                <Col sm={12} xs={24}>
                  <FormItem
                    label="Причина обращения"
                    name={FeedbackFormFields.reason_id}
                    rules={[FORM_ITEM_RULES.required]}
                  >
                    <Select labelInValue>
                      {filteredFeedbackReasons.map(({ id, title }) => (
                        <Select.Option key={id} value={id}>
                          {title}
                        </Select.Option>
                      ))}
                    </Select>
                  </FormItem>
                </Col>
              )}
              <Col span={24}>
                <FormItem
                  label="Комментарий"
                  name={FeedbackFormFields.notes}
                  rules={[
                    FORM_ITEM_RULES.required,
                    FORM_ITEM_RULES.whitespace,
                    FORM_ITEM_RULES.minLength(5),
                    FORM_ITEM_RULES.maxLength(5000),
                  ]}
                >
                  <Input.Textarea placeholder="Ваше сообщение" />
                </FormItem>
              </Col>
              <Col span={24}>
                <FormItem
                  name={FeedbackFormFields.confirm_personal_data_processing_policy}
                  rules={[FORM_ITEM_RULES.required, FORM_ITEM_RULES.term]}
                  valuePropName="checked"
                  className={style.checkboxField}
                >
                  <Checkbox>
                    Даю согласие на{' '}
                    <Typography.Link
                      href={TERMS_CONDITIONS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      обработку своих персональных данных
                    </Typography.Link>
                    *
                  </Checkbox>
                </FormItem>
              </Col>
            </Row>
            {showErrorMessage && (
              <Typography.Paragraph className={style.errorMessage}>
                {status.message}
              </Typography.Paragraph>
            )}
            <div className={style.footer}>
              <Button
                type="primary"
                htmlType="submit"
                disabled={isDisabledButton}
                icon={<Icon name="arrow-1" width={16} height={16} />}
              >
                отправить
              </Button>
              <Typography.Text className={style.explainRequired}>
                * Все поля, отмеченные звездочкой, являются обязательными для заполнения.
              </Typography.Text>
            </div>
          </>
        );
      }}
    </Form>
  );
};

export default FeedbackForm;
